// Category keys, reason codes and handling ids: the baked defaults the served vocabulary overrides.

// ─── Category & Reason Code Constants ─────────────────
export const CATEGORY_KEYS = ['K0', 'K1', 'K2', 'K3A', 'K3B', 'K4', 'KU'] as const;

// Why: KU (the unclassified bucket) is display-only — forcing to KU is nonsensical
export const ASSIGNABLE_CATEGORIES = ['K0', 'K1', 'K2', 'K3A', 'K3B', 'K4'] as const;

export const CATEGORY_LABELS: Record<string, string> = {
  K0: 'Standard-Prozess',
  K1: 'Standard-Info',
  K2: 'Technische Anfrage',
  K3A: 'Beschwerde',
  K3B: 'Eskalation',
  K4: 'Gefahr im Verzug',
  // WIM triage role: primaryAxis = handling id, secondaryAxis[0] = process_type id.
  // NO surface reads this map directly any more — the former triage panel's carve-out (a direct
  // `CATEGORY_LABELS[h] ?? h`) was removed once a tenant could re-word its own vocabulary, because
  // it made the panel the only place a rename could NOT reach; panel and row would have disagreed
  // on one screen. Every consumer now goes through categoryMeta.resolveLabel(), which reaches this
  // tier ONLY for a role that OWNS the legacy K-vocabulary (gMSB/wMSB). A wim ticket with no
  // vocabulary loaded therefore gets the placeholder floor rather than borrowing these words —
  // see categoryMeta.ts's role gate. These wim entries survive as the backstop the wim vocabulary
  // overrides, and as themeContrast's coherence-pin counterpart, not as a rendering path.
  // Ids are collision-free across roles (K-codes vs slugs).
  //
  // RE-KEYED at vocabulary v7 (wim vocabulary v7, 2026-09-13): the handling axis moved from four
  // opaque letter-plus-digit codes to five semantic slugs (auskunft is NEW — the lookup-and-tell
  // outcome that used to be filed as an internal reply), and the process-type axis was re-derived
  // from the client-inbox forensics into the 18 below. There is deliberately NO code→slug mapping
  // anywhere: the demo tenant is purged and re-ingested, so a code-keyed row is not a state that
  // exists — and the retired codes are not spelled anywhere in this bundle, by design.
  // Backstop words carry the canonical German spelling even where the seed still serves an ASCII
  // transliteration — the served row wins at render time, and mirroring the transliteration here
  // would bake a known seed defect into the bundle.
  weiterleiten: 'Weiterleiten',
  antworten: 'Antworten',
  auskunft: 'Auskunft',
  zur_kenntnis: 'Zur Kenntnis',
  pruefen: 'Prüfen',
  anmeldung: 'Anmeldung',
  abmeldung: 'Abmeldung',
  geraeteuebernahme: 'Geräteübernahme',
  zaehlertausch: 'Zählertausch',
  stammdaten: 'Stammdaten / SDÄ',
  stammdaten_anforderung: 'Stammdaten-Anforderung',
  messwerte: 'Messwerte',
  lastgang_bilanzierung: 'Lastgang / Bilanzierung',
  marktnachricht_fehler: 'Marktnachricht-Fehler',
  quotes: 'QUOTES',
  einspeisung_eeg: 'Einspeisung / EEG',
  messkonzept_wandler: 'Messkonzept / Wandler',
  rechnungsausgang: 'Rechnungsausgang',
  lf_sd_zuordnung: 'LF/SD-Zuordnung',
  sondergebiete: 'Sondergebiete',
  beschwerde: 'Beschwerde',
  zustaendigkeitsklaerung: 'Zuständigkeitsklärung',
  sonstiges: 'Sonstiges',
};

// WIM handling ids by MEANING — the single FE enumeration of the handling axis. Comparisons key
// here (forward-eligibility etc.), never to a raw slug at the call site; theme.wimHandling and
// CATEGORY_LABELS' handling entries are keyed against this set by the coherence pin in
// themeContrast. The KEYS are the meanings this frontend reasons about and the VALUES are core's
// HandlingIds slugs — the split is what let the ids stop being opaque codes without a single call
// site changing: `lookup` joined as a fifth meaning, `wimIntern` became `reply` (the word for what
// it does, now that the id no longer needs a code to explain it).
// part 7, 2026-10-13 (operator ruling): the filing handling was renamed Ablegen -> Zur Kenntnis as
// a CONCEPT (slug `zur_kenntnis`, abbr ZK). Its key here is `noted`, not `archive`: `archive` is
// also the name of a DRAFT KIND (the no-letter claim), and one word for two things was the
// confusion the rename removes.
export const WIM_HANDLING = {
  forward: 'weiterleiten',
  reply: 'antworten',
  lookup: 'auskunft',
  noted: 'zur_kenntnis',
  review: 'pruefen',
} as const;

export type WimHandlingId = typeof WIM_HANDLING[keyof typeof WIM_HANDLING];

export const WIM_HANDLING_IDS: readonly WimHandlingId[] = Object.values(WIM_HANDLING);

// The code constant `CLAIM_KIND_HANDLING` that used to live here is served now, as
// `useCategoryMeta().decisionKindHandling` (part 4c, 2026-09-27); `WIM_HANDLING` above stays as the
// handling-id enum.

// FE mirror of core MsbKCatalog.UnclassifiedId: the one key the categorization-performance
// views exclude (KU rides total-reconciliation views only).
export const UNCLASSIFIED_KEY = 'KU';

// Why: EDIFACT signal atoms only exist for K0/K1/K2 (tools/signals/nb/edifact/).
// Without this gate the BE 400s on K3A/K3B/K4 + EDIFACT with "no atoms found".
export const EDIFACT_CATEGORIES = ['K0', 'K1', 'K2'];

export const EDIFACT_FALLBACK_CATEGORY = 'K2';

export const TESTGEN_DEFAULT_TARGET_CATEGORY = 'K2';

export const REASON_CODES = [
  { code: 'termin_status', label: 'Reine Termin-/Statusanfrage', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'standard_prozess', label: 'Standard-Prozess (PIN/Baustrom/Ablesung/HW-Defekt)', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'kontaktformular', label: 'Kontaktformular / Allgemeine Anfrage', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'nicht_zustaendig', label: 'Nicht zuständig / Falsche Adresse', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'irrlaeufer_intern', label: 'Irrläufer (intern)', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'allgemeine_info', label: 'Allgemeine Information (Preise/Datenschutz/iMSys)', minCategory: 'K1', maxCategory: 'K1' },
  { code: 'technische_bewertung', label: 'Technische Bewertung nötig (TAB/RFZ/Klassenzuordnung)', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'systemrecherche', label: 'Systemrecherche / Zählernummer / MaLo-ID', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'koordination_drittpartei', label: 'Koordination mit Installateur / Drittpartei', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'leistungsaenderung', label: 'Leistungsänderung / Wallbox / PV / Einspeisung', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'eigentuemerwechsel', label: 'Eigentümerwechsel / Umzug / Todesfall', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'datenschutz', label: 'Datenschutz / DSGVO / Datenzugriff', minCategory: 'K1', maxCategory: 'K2' },
  { code: 'steuerung_14a', label: 'Steuerung §14a EnWG', minCategory: 'K1', maxCategory: 'K2' },
  { code: 'anhang_verwiesen', label: 'Anhang verwiesen (Inhalt nicht sichtbar)', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'msb_wechsel', label: 'MSB-Wechsel (§5 MsbG)', minCategory: 'K1', maxCategory: 'K3A' },
  { code: 'messstreitigkeit', label: 'Messstreitigkeit / Verbrauchsabweichung', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'abrechnungsstreit', label: 'Abrechnungsstreit / Entgeltforderung', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'rueckrufbitte', label: 'Rückrufbitte', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'vollmacht_vertretung', label: 'Vollmacht / Vertretung / Hausverwaltung', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'mehrere_anliegen', label: 'Mehrere Anliegen gemischt', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'beschwerde', label: 'Beschwerde / Frust (ohne rechtliche Drohung)', minCategory: 'K3A', maxCategory: 'K3A' },
  { code: 'eskalation', label: 'Eskalation: Drohung / Anwalt / Frist / GF-Bezug', minCategory: 'K3B', maxCategory: 'K3B' },
  { code: 'akute_gefahr', label: 'Akute Gefahr (Gas/Brand/Spannung)', minCategory: 'K4', maxCategory: 'K4' },
  { code: 'sonstiges', label: 'Sonstiges', minCategory: undefined, maxCategory: undefined },
  { code: 'unbestimmt', label: 'Nicht bestimmbar', minCategory: undefined, maxCategory: undefined },
] as const;

type ReasonCodeEntry = typeof REASON_CODES[number];

const REASON_CODE_MAP = new Map<string, ReasonCodeEntry>(REASON_CODES.map(rc => [rc.code, rc]));

export const getReasonCodeLabel = (code: string): string =>
  REASON_CODE_MAP.get(code)?.label ?? code;

/** How many secondary-axis pills a surface shows before „+n" (SecondaryAxisPills): a number is a
 *  fixed cap; 'fit' shows as many as the line has room for (the list row and the Archiv row, small
 *  facts on a row 2026-10-19); 'all' never collapses (the detail header). */
export const SECONDARY_AXIS_MAX_VISIBLE = {
  dashboard: 3,
  uploads: 5,
  responses: 'fit',
  detail: 'all',
  chatbot: 3,
} as const;

// Shared with categoryMeta.ts's resolveCategoriesForSecondaryAxisCode (the served-aware seam entry point,
// D10 addendum 4) — both expand a min/max PAIR the same way, whether it came from a baked
// REASON_CODES row or a served grund_code value's own attributes. ASSIGNABLE_CATEGORIES (not a
// re-declared literal) is the one home for "the ordered K-band a range can slice" — see D9 for the
// KU exclusion reasoning that already made it the right set for this.
export const expandCategoryRange = (min: string, max: string): string[] => {
  const minIdx = ASSIGNABLE_CATEGORIES.indexOf(min as typeof ASSIGNABLE_CATEGORIES[number]);
  const maxIdx = ASSIGNABLE_CATEGORIES.indexOf(max as typeof ASSIGNABLE_CATEGORIES[number]);
  if (minIdx < 0 || maxIdx < 0) return [];
  return ASSIGNABLE_CATEGORIES.slice(minIdx, maxIdx + 1) as string[];
};

// THE FLOOR TIER for grund-code -> category-band mapping. categoryMeta.ts's
// resolveCategoriesForSecondaryAxisCode is the served-aware entry point most call sites should use — this
// stays the pure baked lookup it consults when a vocabulary serves no row for the code (or serves
// none at all, the pre-vocabulary-delivery state every MSB tenant is in today).
export const getCategoriesForReasonCode = (code: string): string[] => {
  const rc = REASON_CODE_MAP.get(code);
  if (!rc?.minCategory || !rc?.maxCategory) return [];
  return expandCategoryRange(rc.minCategory, rc.maxCategory);
};

// ─── Status Group Constants ───────────────────────────
export const STATUS_GROUPS = {
  eingereicht: ['submitted', 'ingested'] as string[],
} as const;
